import classNames from 'classnames';
import styles from './VehicleDtcHistory.module.css';
import { Table, ConfigProvider, Spin, Button, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState, useEffect } from 'react';
import { getVehicleStatusModuleHistory } from 'src/app/query.service';
import axios from 'axios';

export interface DataType {
  eventDateTime: string;
  sessionDateTime: string;
  sourceType: string;
  dataReadType: string;
  module: string;
  fullDTCCode: string;
  dtcDescription: string;
  faultTypeDescription: string;
  calDateTimeOfOccurrence: string;
  globalRealTime: string;
  totalDistance: string;
  mainECUVoltage: string;
  engineStatusCrank: string;
  engineStatusRunning: string;
  inCarTemperature: string;
  outsideTemperature: string;
  powerModeQualityFactor: string;
  powerMode: string;
  engineRPM: string;
  vehicleSpeed: string;
  c1: string;
  c2: string;
  c3: string;
  c4: string;
  c5: string;
  c10: string;
  totalDistanceMiles: string;
  totalDistanceKM: string;
  didsSnapshot: string;
  snapshotDecoded: string;
  snapshotDids: string;
  localSignalPublisher: string;
  localSignal: string;
  sourceFile: string;
  status: string;
  dtcHex: string;
  FaultType: string;
  statusHex: string;
  extendedHex: string;
  snapshotHex: string;
  sessionId: string;
}
interface Data {
  [key: string]: any;
}
type FileType = '.zip' | '.csv';
type QueryType = 'session' | 'someOtherType';

interface QueryParameter {
  sessionId: string;
  sessionTimestamp: string;
}
interface VehicleDtcHistoryProps {
  searchedVin: string;
}
const VehicleDtcHistory: React.FC<VehicleDtcHistoryProps> = ({ searchedVin }) => {
  const [data, setData] = useState<DataType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isDownloadPending, setIsDownloadPending] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSession, setSelectedSession] = useState<Data | null>(null);
  const handleRowClick = (record: Data) => {
    setSelectedSession(record);
    setModalVisible(true);
  };

  const getDownloadUrl = (
    fileType: FileType,
    queryType: QueryType,
    // userId: string,
    offset?: number
  ): string => {
    let baseUrl = `${process.env.REACT_APP_API_URL}/viewer/${queryType}`;

    if (queryType === 'session') {
      baseUrl += fileType === '.zip' ? '/summary/export' : '/export';
    } else {
      baseUrl = `${process.env.REACT_APP_API_URL}/${queryType}/query/final/export`;
    }

    // Construct query parameters
    const params = new URLSearchParams();

    // if (userId) {
    //   params.append('userId', userId);
    // }

    if (offset !== undefined) {
      params.append('offset', offset.toString());
    }

    // Append query parameters if they exist
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  };

  const getSddSessionInfo = async (
    queryParameter: { sessionId: string; sessionTimestamp: string },
    // userId: string,
    token: string
  ): Promise<string> => {
    const downloadUrl = `${process.env.REACT_APP_API_URL}/viewer/session/info`;
    // if (userId) {
    //   downloadUrl += `?userId=${encodeURIComponent(userId)}`;
    // }

    const response = await axios.post(downloadUrl, queryParameter, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      responseType: 'text',
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch session info');
    }
  };

  const handleDownloadZip = async () => {
    if (!selectedSession) return;

    // const userId: string = localStorage.getItem('userId') || '';
    const token: string | null = localStorage.getItem('id_token');

    if (!token) {
      console.error('No auth token found!');
      return;
    }

    if (isDownloadPending) return;
    setIsDownloadPending(true);

    try {
      const downloadUrl: string = getDownloadUrl('.zip', 'session');

      const payload = {
        sessionId: selectedSession.sessionId,
        sessionTimestamp: selectedSession.sessionTimestamp,
      };

      const response = await axios.post(downloadUrl, payload, {
        responseType: 'blob', // Ensures the response is treated as a file
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      let fileName = 'download.zip';
      const contentDisposition = response.headers['content-disposition'];

      if (contentDisposition) {
        const filenameRegex = /filename=["']?([^"';]+)["']?/;
        const matches = contentDisposition.match(filenameRegex);

        if (matches && matches[1]) {
          fileName = matches[1]; // Correctly extracts "example.zip"
        }
      }

      // Handle file download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading the file:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsDownloadPending(false);
    }
  };
  const handleViewSession = async () => {
    if (!selectedSession) return;

    setIsLoading(true);

    // const userId: string = decodeURIComponent(localStorage.getItem('userId')) || '';
    const token: string | null = localStorage.getItem('id_token');

    if (!token) {
      console.error('No auth token found!');
      setIsLoading(false);
      return;
    }

    const queryParameter = {
      sessionId: selectedSession.sessionId,
      sessionTimestamp: selectedSession.sessionTimestamp,
    };

    try {
      const sddSummaryHTML = await getSddSessionInfo(queryParameter, token);

      // Open a new window and display the session info
      const windowRef = window.open('/', '_blank', 'scrollbars,resizable,height=800,width=1325');
      if (windowRef) {
        const sessionViewerPlaceholder = `<div style='overflow-y:auto;overflow-x:hidden;height:100%;width:100%'>${sddSummaryHTML}</div>`;
        windowRef.document.write(sessionViewerPlaceholder);
      }
    } catch (err) {
      console.error('Error fetching session info:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: 'Event Date/Time',
      dataIndex: 'eventTimestampString',
      key: 'eventTimestampString',
      fixed: 'left',
      width: '150px',
      onHeaderCell: () => {
        return { style: { minWidth: '150px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Session Date/Time',
      dataIndex: 'sessionTimestampString',
      key: 'sessionTimestampString',
      width: '150px',
      onHeaderCell: () => {
        return { style: { minWidth: '150px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Source Type',
      dataIndex: 'sourceType',
      key: 'sourceType',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Data Read Type',
      dataIndex: 'dataReadType',
      key: 'dataReadType',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Module',
      dataIndex: 'module',
      key: 'module',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Full DTC Code',
      dataIndex: 'dtcFull',
      key: 'dtcFull',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'DTC Description',
      dataIndex: 'dtcDescription',
      key: 'dtcDescription',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Fault Type Description',
      dataIndex: 'faultTypeDescription',
      key: 'faultTypeDescription',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Cal. Date/Time Of Occurrence',
      dataIndex: 'calculatedTimestamp',
      key: 'calculatedTimestamp',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Global Real Time',
      dataIndex: 'globalRealTime',
      key: 'globalRealTime',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Total Distance',
      dataIndex: 'totalDistance',
      key: 'totalDistance',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Main ECU Voltage',
      dataIndex: 'mainEcuVoltage',
      key: 'mainEcuVoltage',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Engine Status Crank',
      dataIndex: 'engineStatusCrank',
      key: 'engineStatusCrank',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Engine Status Running',
      dataIndex: 'engineStatusRunning',
      key: 'engineStatusRunning',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'In Car Temperature',
      dataIndex: 'inCarTemperature',
      key: 'inCarTemperature',
      width: '110px',
      onHeaderCell: () => {
        return { style: { minWidth: '110px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Outside Temperature',
      dataIndex: 'outsideTemperature',
      key: 'outsideTemperature',
      width: '110',
      onHeaderCell: () => {
        return { style: { minWidth: '110' } }; // Set min-width for the column
      },
    },
    {
      title: 'Power Mode Quality Factor',
      dataIndex: 'powerModeQualityFactor',
      key: 'powerModeQualityFactor',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Power Mode',
      dataIndex: 'powerMode',
      key: 'powerMode',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Engine RPM',
      dataIndex: 'engineRpm',
      key: 'engineRpm',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Vehicle Speed',
      dataIndex: 'vehicleSpeed',
      key: 'vehicleSpeed',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'C1',
      dataIndex: 'c1',
      key: 'c1',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'C2',
      dataIndex: 'c2',
      key: 'c2',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'C3',
      dataIndex: 'c3',
      key: 'c3',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'C4',
      dataIndex: 'c4',
      key: 'c4',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'C5',
      dataIndex: 'c5',
      key: 'c5',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'C10',
      dataIndex: 'c10',
      key: 'c10',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Total Distance Miles',
      dataIndex: 'odoMiles',
      key: 'odoMiles',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Total Distance KM',
      dataIndex: 'odoKm',
      key: 'odoKm',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Dids Snapshot',
      dataIndex: 'didsInSnapshot',
      key: 'didsInSnapshot',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Snapshot Decoded',
      dataIndex: 'snapshotDecoded',
      key: 'snapshotDecoded',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },

    {
      title: 'Snapshot Dids',
      dataIndex: 'snapshotDids',
      key: 'snapshotDids',
      width: '100px',
      ellipsis: {
        showTitle: false, // disables Ant Design's default tooltip
      },
      render: (value: string) => {
        const displayText =
          typeof value === 'string' ? value.slice(0, 10) + (value.length > 10 ? '...' : '') : '';
        return <span title={value}>{displayText}</span>;
      },
      onHeaderCell: () => ({
        style: { minWidth: '100px' },
      }),
    },

    {
      title: 'Local Signal Publisher',
      dataIndex: 'localSignalPublisher',
      key: 'localSignalPublisher',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Local Signal',
      dataIndex: 'localSignal',
      key: 'localSignal',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Source File',
      dataIndex: 'sourceFile',
      key: 'sourceFile',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'DTC (hex)',
      dataIndex: 'dtcHex',
      key: 'dtcHex',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Fault Type (hex)',
      dataIndex: 'faultTypeHex',
      key: 'faultTypeHex',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Status (hex)',
      dataIndex: 'statusHex',
      key: 'statusHex',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Extended (HEX)',
      dataIndex: 'extendedHex',
      key: 'extendedHex',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Snapshot (hex)',
      dataIndex: 'snapshotHex',
      key: 'snapshotHex',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Session ID',
      dataIndex: 'sessionId',
      key: 'sessionId',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
  ];
  const getVehicleDtcHistory = async () => {
    const userId = localStorage.getItem('userId');
    try {
      setIsLoading(true);
      const result: any = await getVehicleStatusModuleHistory('dtc', searchedVin);
      result.items.forEach((n: any) => {
        n.sessionTimestampString = new Date(n.sessionTimestamp).toLocaleString();
        n.eventTimestampString = new Date(n.eventTimestamp).toLocaleString();
      });
      const sortedData = result.items.sort((a: any, b: any) => {
        return new Date(b.eventTimestamp).getTime() - new Date(a.eventTimestamp).getTime();
      });

      setData([...sortedData]);
      setIsLoading(false);
    } catch (err) {
      console.log('error...', err);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    getVehicleDtcHistory();
    let lastCopied = '';
    let messageVisible = false;
    let hideTimeout: NodeJS.Timeout;

    const handleMouseUp = () => {
      const selectedText = window.getSelection()?.toString().trim();

      if (selectedText && selectedText !== lastCopied) {
        lastCopied = selectedText;

        navigator.clipboard
          .writeText(selectedText)
          .then(() => {
            if (!messageVisible) {
              messageVisible = true;
              message.destroy(); // remove any lingering messages
              message.success('Copied to clipboard', 1.2);

              hideTimeout = setTimeout(() => {
                messageVisible = false;
              }, 1200); // match toast duration
            }
          })
          .catch((err) => {
            console.error('Clipboard copy failed:', err);
            message.destroy();
            message.error('Failed to copy');
          });
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      clearTimeout(hideTimeout);
    };
  }, []);
  const tableTheme = {
    components: {
      Table: {
        headerBg: '#dcdcdc',
        headerSplitColor: 'white',
        headerBorderRadius: 0,
        cellFontSize: 14,
      },
    },
  };

  // TODO: Implement logic to populate data in the table with api call
  return (
    <div className={classNames(styles.VehicleDtcHistory)}>
      <ConfigProvider theme={tableTheme}>
        <Table<DataType>
          columns={columns}
          dataSource={data}
          scroll={{ x: `max-content`, y: 300 }}
          pagination={false}
          rowKey="id"
          loading={{
            spinning: isLoading,
            indicator: <Spin className={classNames(styles['loader-color'])} />,
          }}
          onRow={(record) => ({
            onClick: (e) => {
              const selection = window.getSelection();
              const isTextSelected = selection && selection.toString().length > 0;

              if (!isTextSelected) {
                handleRowClick(record);
              }
            },
          })}
        />
      </ConfigProvider>
      <Modal
        title="Session"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedSession && (
          <div>
            <p>{selectedSession.sessionId}</p>
            {selectedSession.sourceType == 'Topix' ? (
              <div className={classNames(styles['VehicleDtcHistory--topix-notes-container'])}>
                <p>
                  TOPIX data cannot currently be downloaded in VFDB, please use{' '}
                  <a href="https://topix.jaguar.jlrext.com" target="_blank" rel="noreferrer">
                    topix.jaguar.jlrext.com
                  </a>
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <Button
                  onClick={handleViewSession}
                  className={classNames(styles['VehicleDtcHistory--Buttons'])}
                >
                  View Session
                </Button>
                <Button
                  onClick={handleDownloadZip}
                  className={classNames(styles['VehicleDtcHistory--Buttons'])}
                  style={{ marginRight: 20 }}
                >
                  Download ZIP
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VehicleDtcHistory;
